import type { NotificationInput, Notification } from '@/stores/types';

import { logger } from '@/lib/logger';
// UUID v4生成関数
function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

export interface NotificationService {
  createNotification: (notification: NotificationInput) => Promise<Notification>;
  scheduleRemoval: (id: string, delay: number) => () => void;
}

export function createNotificationService(): NotificationService {
  const timers = new Map<string, NodeJS.Timeout>();

  return {
    async createNotification(notification: NotificationInput): Promise<Notification> {
      // 本番環境では実際のサーバーAPIを呼び出す
      // 開発環境では、IDはサーバー側で生成されるべき
      const id = process.env.NODE_ENV === 'development' 
        ? generateUUID()
        : await generateServerSideId();

      if (process.env.NODE_ENV === 'production' && id.startsWith('dev-')) {
        logger.warn('Client-side ID generation detected in production');
      }

      return {
        id,
        ...notification,
        timestamp: new Date(),
        read: false,
      };
    },

    scheduleRemoval(id: string, delay: number): () => void {
      // 既存のタイマーをクリア
      if (timers.has(id)) {
        clearTimeout(timers.get(id)!);
      }

      // 新しいタイマーを設定
      const timer = setTimeout(() => {
        timers.delete(id);
        // 実際の削除処理はストアのアクションで行う
      }, delay);

      timers.set(id, timer);

      // キャンセル関数を返す
      return () => {
        if (timers.has(id)) {
          clearTimeout(timers.get(id)!);
          timers.delete(id);
        }
      };
    },
  };
}

// サーバーサイドID生成のシミュレーション
async function generateServerSideId(): Promise<string> {
  // 実際の実装では、サーバーAPIを呼び出す
  return generateUUID();
}

// テスト用のモックサービス（vitest globals: true 下で vi が global にある前提）
export function createMockNotificationService(): NotificationService {
  const { vi } = globalThis as unknown as { vi: typeof import('vitest').vi };
  return {
    createNotification: vi.fn().mockResolvedValue({
      id: 'mock-id',
      type: 'info',
      message: 'Mock notification',
      createdAt: new Date().toISOString(),
    }),
    scheduleRemoval: vi.fn().mockReturnValue(() => {}),
  };
}